import {useCallback} from "react";
import type {Dayjs} from "dayjs";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import {useBulkEnrollUsers, useEnrollCohortInCourse, useManageDueDates, type ManageDueDatesBody} from "@/app2/shared/data";
import useCustomSnackbar from "@/snackbars/useCustomSnackbar";
import type {ManageDueDatesMode} from "../components/ManageDueDatesModal";

const PRIORITIZE_SCHEDULE = "prioritize-schedule" as const;

function formatEnrollmentDueDate(dueDate: Dayjs): string {
	return dueDate.format("YYYY-MM-DD");
}

function resolveDueDateParam(dueDate: Dayjs | null, showDueDateSection: boolean): string | undefined {
	if (!showDueDateSection || !dueDate) {
		return undefined;
	}
	return formatEnrollmentDueDate(dueDate);
}

function buildManageDueDatesBody(
	userIds: number[],
	mode: ManageDueDatesMode,
	dueDate: Dayjs | null
): ManageDueDatesBody {
	if (mode === "set" && dueDate) {
		return {userIds, dueDate: formatEnrollmentDueDate(dueDate)};
	}
	return {userIds};
}

export function useCourseEnrollmentActions(courseId: number): {
	enrollUsers: (
		params: {userIds: number[]; dueDate: Dayjs | null; showDueDateSection: boolean},
		options?: {onSuccess?: () => void}
	) => void;
	enrollCohort: (
		params: {cohortId: number; dueDate: Dayjs | null; showDueDateSection: boolean},
		options?: {onSuccess?: () => void}
	) => void;
	manageDueDates: (
		params: {userIds: number[]; mode: ManageDueDatesMode; dueDate: Dayjs | null},
		options?: {onSuccess?: () => void}
	) => void;
	isBulkPending: boolean;
	isCohortPending: boolean;
	isManageDueDatesPending: boolean;
} {
	const {t} = useSSTranslation();
	const {successSnackbar, errorSnackbar, warningSnackbar, infoSnackbar} = useCustomSnackbar();
	const bulkEnrollMutation = useBulkEnrollUsers(courseId);
	const enrollCohortMutation = useEnrollCohortInCourse(courseId);
	const manageDueDatesMutation = useManageDueDates(courseId);

	const enrollUsers = useCallback(
		(
			params: {userIds: number[]; dueDate: Dayjs | null; showDueDateSection: boolean},
			options?: {onSuccess?: () => void}
		) => {
			const {userIds, dueDate, showDueDateSection} = params;

			bulkEnrollMutation.mutate(
				{
					userIds,
					enrollmentType: PRIORITIZE_SCHEDULE,
					dueDate: resolveDueDateParam(dueDate, showDueDateSection)
				},
				{
					onSuccess: (response) => {
						const enrolledCount = response.enrollmentIds?.length ?? 0;
						const errorCount = response.errors?.length ?? 0;

						if (errorCount === 0) {
							successSnackbar(t("bulk_enrollment_success", "Enrolled {{count}} user(s)", {count: enrolledCount}));
						} else {
							const message = t(
								"bulk_enrollment_partial_success",
								"Enrolled {{enrolled}} user(s), {{failed}} failed",
								{enrolled: enrolledCount, failed: errorCount}
							);
							if (enrolledCount === 0) {
								errorSnackbar(message);
							} else {
								warningSnackbar(message);
							}
						}

						options?.onSuccess?.();
					},
					onError: (error) => {
						const message = error instanceof Error ? error.message : String(error);
						errorSnackbar(t("bulk_enrollment_error", "Failed to enroll users: {{message}}", {message}));
					}
				}
			);
		},
		[bulkEnrollMutation, successSnackbar, errorSnackbar, warningSnackbar, t]
	);

	const enrollCohort = useCallback(
		(
			params: {cohortId: number; dueDate: Dayjs | null; showDueDateSection: boolean},
			options?: {onSuccess?: () => void}
		) => {
			const {cohortId, dueDate, showDueDateSection} = params;

			enrollCohortMutation.mutate(
				{
					cohortId,
					enrollmentType: PRIORITIZE_SCHEDULE,
					dueDate: resolveDueDateParam(dueDate, showDueDateSection)
				},
				{
					onSuccess: (data) => {
						if (data.inProgress && data.userCount != null) {
							successSnackbar(
								t(
									"enroll_cohort_started",
									"Enrollment started for {{count}} user(s). You'll be notified when complete.",
									{count: data.userCount}
								)
							);
						} else {
							successSnackbar(t("enroll_cohort_success", "Cohort enrolled successfully."));
						}
						options?.onSuccess?.();
					},
					onError: (error) => {
						errorSnackbar(
							t("enroll_cohort_error", "Failed to enroll cohort: {{message}}", {message: error.message})
						);
					}
				}
			);
		},
		[enrollCohortMutation, successSnackbar, errorSnackbar, t]
	);

	const manageDueDates = useCallback(
		(
			params: {userIds: number[]; mode: ManageDueDatesMode; dueDate: Dayjs | null},
			options?: {onSuccess?: () => void}
		) => {
			const {userIds, mode, dueDate} = params;

			manageDueDatesMutation.mutate(buildManageDueDatesBody(userIds, mode, dueDate),
				{
					onSuccess: (response) => {
						const updatedCount = response.enrollmentIds?.length ?? 0;
						const errorCount = response.errors?.length ?? 0;

						if (errorCount === 0) {
							if (updatedCount === 0) {
								infoSnackbar(t("manage_due_dates_no_changes", "No changes were needed."));
							} else {
								successSnackbar(
									t("manage_due_dates_success", "Updated due dates for {{count}} learner(s)", {
										count: updatedCount
									})
								);
							}
						} else {
							const message = t(
								"manage_due_dates_partial_errors",
								"Updated {{updated}} learner(s), {{failed}} failed",
								{updated: updatedCount, failed: errorCount}
							);
							if (updatedCount === 0) {
								errorSnackbar(message);
							} else {
								warningSnackbar(message);
							}
						}

						options?.onSuccess?.();
					},
					onError: (error) => {
						const message = error instanceof Error ? error.message : String(error);
						errorSnackbar(
							t("manage_due_dates_error", "Failed to manage due dates: {{message}}", {message})
						);
					}
				}
			);
		},
		[manageDueDatesMutation, successSnackbar, errorSnackbar, warningSnackbar, infoSnackbar, t]
	);

	return {
		enrollUsers,
		enrollCohort,
		manageDueDates,
		isBulkPending: bulkEnrollMutation.isPending,
		isCohortPending: enrollCohortMutation.isPending,
		isManageDueDatesPending: manageDueDatesMutation.isPending
	};
}
